import { useCallback, useRef, useState } from "react";
import useAxiosClient from "../axios-client";
import { toast } from "react-toastify";

/**
 * The patterns of a fabric, fetched on demand and kept per fabric.
 *
 * Unlike the other reference lists this one is not loaded once at mount: a
 * pattern belongs to a fabric, every product line may name a different one, and
 * a book runs to hundreds of rows whose swatch links are signed and expire. So
 * the list is fetched the first time a line asks for a given fabric and held
 * for the rest of the form — a form with six lines of the same fabric costs one
 * request, not six.
 *
 * `inFlight` tracks requests rather than fabrics already fetched, so two lines
 * switching to the same fabric in the same tick don't both fire.
 */
export const useFabricPatterns = () => {
    const axiosClient = useAxiosClient();
    const [byFabric, setByFabric] = useState({});
    const [loadingFabric, setLoadingFabric] = useState(null);
    const inFlight = useRef({});

    const loadPatterns = useCallback(async (fabricTypeId) => {
        if (!fabricTypeId) return;
        if (inFlight.current[fabricTypeId]) return;

        inFlight.current[fabricTypeId] = true;
        setLoadingFabric(fabricTypeId);

        try {
            const response = await axiosClient.get('/settings/fabricPatterns/index', {
                params: { fabricType: fabricTypeId },
            });
            setByFabric((prev) => ({ ...prev, [fabricTypeId]: response.data.data || [] }));
        } catch (error) {
            // Leave the fabric unfetched so selecting it again retries; a failed
            // swatch list must not permanently empty the picker.
            delete inFlight.current[fabricTypeId];
            const message = (error.response && error.response.data && error.response.data.message)
                || error.message || error.toString();
            toast.error(message);
        } finally {
            setLoadingFabric((current) => (current === fabricTypeId ? null : current));
        }
    }, [axiosClient]);

    const patternsFor = useCallback(
        (fabricTypeId) => byFabric[fabricTypeId] || [],
        [byFabric],
    );

    return { patternsFor, loadPatterns, loadingFabric };
};
